import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notificationAPI, authAPI } from '../lib/api';
import { registerServiceWorker, subscribeToPush } from '../lib/pushService';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { PasswordInput } from './ui/password-input';
import { Label } from './ui/label';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  LayoutDashboard,
  Users,
  Calendar,
  FileText,
  CreditCard,
  BarChart3,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Settings,
  Bell,
  ScrollText,
  ClipboardCheck,
  Map,
  Lock,
  User,
  MessageCircle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Layout = ({ children }) => {
  const { user, logout, clearMustChangePassword } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);

  // Change password modal
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [pwData, setPwData] = useState({ current: '', newPw: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');

  // Show forced password change on first load if needed
  useEffect(() => {
    if (user?.must_change_password) {
      setChangePwOpen(true);
    }
  }, [user?.must_change_password]);

  const handleChangePassword = async () => {
    setPwError('');
    if (pwData.newPw.length < 6) { setPwError('New password must be at least 6 characters'); return; }
    if (pwData.newPw !== pwData.confirm) { setPwError('Passwords do not match'); return; }
    setPwSaving(true);
    try {
      await authAPI.changePassword(pwData.current, pwData.newPw);
      clearMustChangePassword();
      setChangePwOpen(false);
      setPwData({ current: '', newPw: '', confirm: '' });
    } catch (e) {
      setPwError(e.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwSaving(false);
    }
  };

  // Register service worker and subscribe to push on mount
  const [pushStatus, setPushStatus] = useState('checking');
  useEffect(() => {
    registerServiceWorker().then(async () => {
      if (user) {
        const sub = await subscribeToPush();
        if (sub) {
          setPushStatus('subscribed');
        } else if ('Notification' in window && Notification.permission === 'denied') {
          setPushStatus('denied');
        } else {
          setPushStatus('not_subscribed');
        }
      }
    });
  }, [user]);

  const requestPush = async () => {
    if ('Notification' in window) {
      const perm = await Notification.requestPermission();
      if (perm === 'granted') {
        const sub = await subscribeToPush();
        if (sub) setPushStatus('subscribed');
      } else {
        setPushStatus('denied');
      }
    }
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationAPI.getAll();
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Global chat WebSocket for message notification sounds
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !user?.id) return;

    // Play notification sound at max volume — creates fresh Audio each time
    // to avoid iOS/Safari stale-audio issues
    const playNotifSound = () => {
      try {
        const audio = new Audio('/notification.wav?v=3');
        audio.volume = 1.0;
        audio.play().catch(() => {});
      } catch {}
    };

    const wsProtocol = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/chat/ws/chat/${token}`;

    let ws;
    let reconnectTimer;
    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'new_message' && data.message?.sender_id !== user.id) {
              // Play loud notification sound
              playNotifSound();
              // Update app badge count
              if ('setAppBadge' in navigator) {
                navigator.setAppBadge().catch(() => {});
              }
              // Show browser notification if permitted
              if ('Notification' in window && Notification.permission === 'granted') {
                const senderName = data.message?.sender_name || 'Someone';
                const msgText = data.message?.text || 'Sent a file';
                new Notification(`Message from ${senderName}`, {
                  body: msgText.slice(0, 100),
                  icon: '/logo192.png',
                  tag: 'chat-' + data.conversation_id,
                  renotify: true,
                  silent: false,
                });
              }
            }
          } catch {}
        };
        ws.onclose = () => {
          reconnectTimer = setTimeout(connect, 5000);
        };
      } catch {}
    };
    connect();

    return () => {
      clearTimeout(reconnectTimer);
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, [user?.id]);

  const isAdmin = user?.role === 'admin';

  const adminNavItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: Users, label: 'Employees', path: '/admin/employees' },
    { icon: Calendar, label: 'Attendance', path: '/admin/attendance' },
    { icon: FileText, label: 'Leave', path: '/admin/leave' },
    { icon: Settings, label: 'Leave Settings', path: '/admin/leave-settings' },
    { icon: CreditCard, label: 'Expenses', path: '/admin/expenses' },
    { icon: ScrollText, label: 'Policies', path: '/admin/policies' },
    { icon: ClipboardCheck, label: 'Evaluations', path: '/admin/evaluations' },
    { icon: BarChart3, label: 'Reports', path: '/admin/reports' },
    { icon: MessageCircle, label: 'Conversations', path: '/conversations' },
  ];

  const adminPortalItems = [
    { icon: LayoutDashboard, label: 'My Home', path: '/portal' },
    { icon: Calendar, label: 'My Attendance', path: '/portal/attendance' },
    { icon: FileText, label: 'My Leave', path: '/portal/leave' },
    { icon: CreditCard, label: 'My Expenses', path: '/portal/expenses' },
    { icon: User, label: 'My Profile', path: '/portal/profile' },
  ];

  const employeeNavItems = [
    { icon: LayoutDashboard, label: 'Home', path: '/portal' },
    { icon: Calendar, label: 'Attendance', path: '/portal/attendance' },
    { icon: FileText, label: 'Leave', path: '/portal/leave' },
    { icon: CreditCard, label: 'Expenses', path: '/portal/expenses' },
    { icon: User, label: 'My Profile', path: '/portal/profile' },
    { icon: MessageCircle, label: 'Conversations', path: '/conversations' },
  ];

  const navItems = isAdmin ? adminNavItems : employeeNavItems;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getCompanyLabel = (company) => {
    switch (company) {
      case 'empact_studio':
        return 'Empact Studio';
      case 'event_resource':
        return 'Event Resource';
      case 'both':
        return 'Empact Studio & Event Resource';
      default:
        return company;
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    return `${BACKEND_URL}${photo}`;
  };

  const handleNotifClick = async (notif) => {
    if (!notif.is_read) {
      await notificationAPI.markRead(notif.id);
      fetchNotifications();
    }
  };

  const handleMarkAllRead = async () => {
    await notificationAPI.markAllRead();
    fetchNotifications();
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="app-layout">
      {/* Push notification permission banner */}
      {pushStatus === 'not_subscribed' && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-[#1D35A0] text-white px-4 py-2.5 flex items-center justify-between text-sm shadow-lg" data-testid="push-permission-banner">
          <span>Enable notifications to get alerts even when the app is closed</span>
          <div className="flex items-center gap-2 ml-3 shrink-0">
            <button onClick={requestPush} className="bg-[#F5AB00] text-[#1D35A0] font-semibold px-3 py-1 rounded-lg hover:bg-yellow-400 transition-colors" data-testid="enable-notifications-btn">
              Enable
            </button>
            <button onClick={() => setPushStatus('dismissed')} className="text-white/70 hover:text-white px-1">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setSidebarOpen(false)}
          data-testid="mobile-overlay"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 sidebar transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center justify-center h-16 px-4 border-b border-white/10">
          <Link to={isAdmin ? '/admin/employees' : '/portal'} className="flex items-center justify-center mx-auto">
            <div className="flex items-center justify-center gap-2 bg-white rounded-lg px-3 py-2">
              <img src="/es-logo.png" alt="ES" className="h-9 object-contain" />
              <div className="w-px h-8 bg-slate-200" />
              <img src="/er-logo.png" alt="ER" className="h-9 object-contain" />
            </div>
          </Link>
          <button
            className="lg:hidden text-white/70 hover:text-white absolute right-4"
            onClick={() => setSidebarOpen(false)}
            data-testid="close-sidebar-btn"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 64px - 72px)' }} data-testid="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
                {item.placeholder && (
                  <span className="ml-auto text-xs bg-white/10 px-2 py-0.5 rounded text-slate-400">
                    Soon
                  </span>
                )}
              </Link>
            );
          })}

          {/* My Portal section for admins */}
          {isAdmin && (
            <>
              <div className="pt-4 pb-2">
                <p className="px-3 text-[10px] font-semibold uppercase tracking-widest text-slate-400">My Portal</p>
              </div>
              {adminPortalItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sidebar-link ${isActive ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* User section at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10 border-2 border-white/20">
              <AvatarImage src={getPhotoUrl(user?.profile_photo)} />
              <AvatarFallback className="bg-[#1D35A0] text-white text-sm">
                {getInitials(user?.full_name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-400 truncate">{getCompanyLabel(user?.company)}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64 min-h-screen bg-slate-50">
        {/* Header */}
        <header className="sticky top-0 z-40 h-14 bg-white/80 backdrop-blur-xl border-b border-slate-200 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-2">
            <button
              className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
              onClick={() => setSidebarOpen(true)}
              data-testid="open-sidebar-btn"
            >
              <Menu className="w-6 h-6 text-slate-700" />
            </button>
            <button
              className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
              onClick={() => navigate('/conversations')}
              data-testid="header-chat-btn"
              title="Conversations"
            >
              <MessageCircle className={`w-5 h-5 ${location.pathname === '/conversations' ? 'text-[#1D35A0]' : 'text-slate-600'}`} />
            </button>
          </div>

          <div className="flex items-center gap-2">
            {/* Notifications Bell */}
            <div className="relative" data-testid="notification-bell-wrapper">
              <button
                onClick={() => setNotifOpen(!notifOpen)}
                className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
                data-testid="notification-bell-btn"
              >
                <Bell className="w-5 h-5 text-slate-600" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                  <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-14 sm:top-full sm:mt-2 sm:w-80 bg-white rounded-lg border border-slate-200 shadow-xl z-50 overflow-hidden" data-testid="notification-dropdown">
                    <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                      <span className="text-sm font-semibold text-[#1D35A0]">Notifications</span>
                      {unreadCount > 0 && (
                        <button onClick={handleMarkAllRead} className="text-xs text-blue-600 hover:text-blue-700" data-testid="mark-all-read-btn">
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="max-h-[360px] overflow-y-auto divide-y divide-slate-100">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center text-slate-400 text-sm">No notifications</div>
                      ) : (
                        notifications.slice(0, 20).map(n => (
                          <button
                            key={n.id}
                            onClick={() => handleNotifClick(n)}
                            className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${!n.is_read ? 'bg-blue-50/50' : ''}`}
                            data-testid={`notif-${n.id}`}
                          >
                            <div className="flex items-start gap-2">
                              {!n.is_read && <span className="w-2 h-2 mt-1.5 rounded-full bg-blue-500 shrink-0" />}
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-800">{n.title}</p>
                                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                                <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex items-center gap-2 hover:bg-slate-100"
                data-testid="user-menu-btn"
              >
                <Avatar className="w-8 h-8">
                  <AvatarImage src={getPhotoUrl(user?.profile_photo)} />
                  <AvatarFallback className="bg-[#1D35A0] text-white text-xs">
                    {getInitials(user?.full_name)}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden md:block text-sm font-medium text-slate-700">
                  {user?.full_name}
                </span>
                <ChevronDown className="w-4 h-4 text-slate-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2">
                <p className="text-sm font-medium">{user?.full_name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {user?.designation} • {getCompanyLabel(user?.company)}
                </p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => { setChangePwOpen(true); setPwData({ current: '', newPw: '', confirm: '' }); setPwError(''); }}
                className="cursor-pointer"
                data-testid="change-password-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="main-content p-4 lg:p-5 pb-24 lg:pb-5" data-testid="main-content">
          {pushStatus !== 'subscribed' && pushStatus !== 'checking' && (
            <div className="mb-4 bg-amber-50 border border-amber-300 text-amber-800 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-2 sm:gap-3 text-center sm:text-left" data-testid="push-notification-banner">
              <div className="flex items-center gap-2 text-xs sm:text-sm justify-center sm:justify-start">
                <Bell className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                <span className="font-medium">
                  {pushStatus === 'denied'
                    ? 'Notifications are blocked. Please enable them in your browser settings — it is mandatory.'
                    : 'Push notifications are required. Please enable them to receive important updates.'}
                </span>
              </div>
              {pushStatus !== 'denied' && (
                <Button size="sm" onClick={requestPush} className="bg-amber-600 hover:bg-amber-700 text-white shrink-0 text-xs sm:text-sm" data-testid="enable-push-btn">
                  Enable Now
                </Button>
              )}
            </div>
          )}
          {children}
        </main>
      </div>

      {/* Mobile bottom quick-access bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 shadow-[0_-2px_10px_rgba(0,0,0,0.06)] lg:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around h-14 px-1">
          <button
            onClick={() => navigate('/portal')}
            className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors ${
              (location.pathname === '/admin' || location.pathname === '/portal') ? 'text-[#1D35A0]' : 'text-slate-500'
            }`}
            data-testid="bottom-nav-home"
          >
            <LayoutDashboard className="w-5 h-5" />
            <span className="text-[10px] font-medium">Home</span>
          </button>
          <button
            onClick={() => navigate('/portal/attendance')}
            className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors ${
              location.pathname.includes('/attendance') ? 'text-[#1D35A0]' : 'text-slate-500'
            }`}
            data-testid="bottom-nav-attendance"
          >
            <Calendar className="w-5 h-5" />
            <span className="text-[10px] font-medium">Attendance</span>
          </button>
          <button
            onClick={() => navigate('/portal/leave')}
            className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors ${
              location.pathname.includes('/leave') ? 'text-[#1D35A0]' : 'text-slate-500'
            }`}
            data-testid="bottom-nav-leave"
          >
            <FileText className="w-5 h-5" />
            <span className="text-[10px] font-medium">Leave</span>
          </button>
          <button
            onClick={() => navigate('/portal/expenses')}
            className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors ${
              location.pathname.includes('/expense') ? 'text-[#1D35A0]' : 'text-slate-500'
            }`}
            data-testid="bottom-nav-expenses"
          >
            <CreditCard className="w-5 h-5" />
            <span className="text-[10px] font-medium">Expenses</span>
          </button>
          <button
            onClick={() => navigate('/conversations')}
            className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors ${
              location.pathname === '/conversations' ? 'text-[#1D35A0]' : 'text-slate-500'
            }`}
            data-testid="bottom-nav-chat"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-[10px] font-medium">Chat</span>
          </button>
        </div>
      </div>

      {/* Force Change Password Modal */}
      <Dialog open={changePwOpen} onOpenChange={(open) => { if (!user?.must_change_password) setChangePwOpen(open); }}>
        <DialogContent className="max-w-md" onInteractOutside={(e) => { if (user?.must_change_password) e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1D35A0]">
              <Lock className="w-5 h-5" />
              Change Your Password
            </DialogTitle>
            <DialogDescription>
              {user?.must_change_password
                ? 'You must change your password before continuing.'
                : 'Update your account password.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <PasswordInput
                value={pwData.current}
                onChange={(e) => setPwData(p => ({ ...p, current: e.target.value }))}
                placeholder="Enter current password"
                data-testid="pw-current"
              />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <PasswordInput
                value={pwData.newPw}
                onChange={(e) => setPwData(p => ({ ...p, newPw: e.target.value }))}
                placeholder="Min 6 characters"
                data-testid="pw-new"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <PasswordInput
                value={pwData.confirm}
                onChange={(e) => setPwData(p => ({ ...p, confirm: e.target.value }))}
                placeholder="Repeat new password"
                data-testid="pw-confirm"
              />
            </div>
            {pwError && <p className="text-sm text-red-600" data-testid="pw-error">{pwError}</p>}
            <div className="flex justify-end gap-2 pt-2">
              {!user?.must_change_password && (
                <Button variant="outline" onClick={() => setChangePwOpen(false)}>Cancel</Button>
              )}
              <Button
                onClick={handleChangePassword}
                className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
                disabled={pwSaving || !pwData.current || !pwData.newPw || !pwData.confirm}
                data-testid="pw-submit"
              >
                {pwSaving ? 'Saving...' : 'Change Password'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
